// Brand Controls redesign — Quick Setup tab.
//
// Extraction state machine: idle -> analyzing -> done. "analyzing" is
// cosmetic progress driven off the single pending request (isGenerating),
// not real substeps. The done state renders the real generateBrandVoice
// result (draft profile attrs + before/after) bound from the orchestrator.

import { useEffect, useState } from "react";
import { Sparkles, Globe, Upload, ChevronRight, Check, Save, CheckCircle2 } from "lucide-react";
import type {
  VoiceProfile,
  GeneratedAttributes,
  BeforeAfterExample,
} from "../../../services/brand/brand-voice-service";
import { ExtractedAttrCards } from "./shared";

export interface QuickSetupViewProps {
  websiteUrl: string;
  onUrl: (v: string) => void;
  sampleText: string;
  onSample: (v: string) => void;
  advancedOpen: boolean;
  toggleAdvanced: () => void;
  uploadName: string | null;
  onUpload: (f: File | null) => void;
  generate: () => void;
  reset: () => void;
  isGenerating: boolean;
  showResult: boolean;
  domain: string;
  profile: VoiceProfile | null;
  attrs: GeneratedAttributes | null;
  beforeAfter: BeforeAfterExample | null;
  save: () => void;
  saved: boolean;
}

const STEP_DEF = [
  { done: "Validated your URL", doing: "Validating your URL..." },
  { done: "Extracted writing signals", doing: "Extracting writing signals..." },
  { done: "Drafted your voice profile", doing: "Drafting your voice profile..." },
];

export function QuickSetupView(props: QuickSetupViewProps) {
  const {
    websiteUrl,
    onUrl,
    sampleText,
    onSample,
    advancedOpen,
    toggleAdvanced,
    uploadName,
    onUpload,
    generate,
    reset,
    isGenerating,
    showResult,
    domain,
    profile,
    attrs,
    beforeAfter,
    save,
    saved,
  } = props;

  // Cosmetic step animation while the request is pending.
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (!isGenerating) {
      setStep(0);
      return;
    }
    setStep(0);
    const id = setInterval(() => {
      setStep((s) => {
        if (s >= 2) {
          clearInterval(id);
          return 2;
        }
        return s + 1;
      });
    }, 800);
    return () => clearInterval(id);
  }, [isGenerating]);

  const ba = beforeAfter || profile?.beforeAfterExample || null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div
        style={{
          border: "1px solid var(--border)",
          borderRadius: 16,
          overflow: "hidden",
          background: "var(--card)",
          boxShadow: "var(--elevation-sm)",
        }}
      >
        <div style={{ padding: "24px 26px" }}>
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--muted-foreground)", marginBottom: 8 }}>
              Extract from your content
            </div>
            <h2 style={{ fontSize: 19, fontWeight: 600, letterSpacing: "-.01em", color: "var(--foreground)", margin: "0 0 4px" }}>
              Point me at your brand and I'll detect the voice.
            </h2>
            <p style={{ fontSize: 14, color: "var(--muted-foreground)", margin: 0, lineHeight: 1.5 }}>
              Give me a URL and I'll read your live content, detect how you actually write, and draft a profile you can refine. Takes about 30 seconds.
            </p>
          </div>

          {/* idle */}
          {!isGenerating && !showResult && (
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--muted-foreground)", marginBottom: 8 }}>
                Your website or product page
              </label>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <div style={{ position: "relative", flex: 1, minWidth: 240 }}>
                  <Globe style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", width: 16, height: 16, color: "var(--muted-foreground)" }} strokeWidth={1.7} />
                  <input
                    className="bc-input"
                    type="url"
                    value={websiteUrl}
                    onChange={(e) => onUrl(e.target.value)}
                    placeholder="https://yourbrand.com"
                    style={{
                      width: "100%",
                      height: 50,
                      padding: "0 15px 0 40px",
                      border: "1px solid var(--border)",
                      borderRadius: 11,
                      background: "var(--background)",
                      fontSize: 15,
                      fontFamily: "var(--font-sans)",
                      color: "var(--foreground)",
                      outline: "none",
                    }}
                  />
                </div>
                <button
                  onClick={generate}
                  disabled={!websiteUrl.trim()}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#fff",
                    background: "var(--orb-gradient)",
                    border: "none",
                    borderRadius: 11,
                    padding: "0 20px",
                    height: 50,
                    cursor: websiteUrl.trim() ? "pointer" : "default",
                    opacity: websiteUrl.trim() ? 1 : 0.6,
                    boxShadow: "var(--shadow-spectral-violet-glow)",
                  }}
                >
                  <Sparkles style={{ width: 15, height: 15 }} strokeWidth={1.8} />
                  Extract my voice
                </button>
              </div>

              <button
                onClick={toggleAdvanced}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 14, fontSize: 12.5, fontWeight: 600, color: "var(--muted-foreground)", background: "none", border: "none", cursor: "pointer", padding: 0 }}
              >
                <span style={{ display: "inline-flex", transition: "transform var(--duration-base)", transform: advancedOpen ? "rotate(90deg)" : "rotate(0deg)" }}>
                  <ChevronRight style={{ width: 14, height: 14 }} strokeWidth={2} />
                </span>
                Or paste writing samples / upload a file instead
              </button>

              {advancedOpen && (
                <div style={{ marginTop: 14 }}>
                  <textarea
                    className="bc-input"
                    value={sampleText}
                    onChange={(e) => onSample(e.target.value)}
                    placeholder="Paste sample content: product descriptions, blog posts, support articles..."
                    rows={6}
                    style={{
                      width: "100%",
                      padding: "12px 14px",
                      border: "1px solid var(--border)",
                      borderRadius: 11,
                      background: "var(--background)",
                      fontSize: 14,
                      fontFamily: "var(--font-sans)",
                      color: "var(--foreground)",
                      outline: "none",
                      resize: "vertical",
                    }}
                  />
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 600, color: "var(--foreground)", background: "var(--background)", border: "1px solid var(--border)", borderRadius: 9, padding: "9px 14px", cursor: "pointer" }}>
                      <Upload style={{ width: 14, height: 14, color: "var(--muted-foreground)" }} strokeWidth={1.7} />
                      {uploadName ? "Change file" : "Upload file"}
                      <input
                        type="file"
                        accept=".txt,.docx,.pdf"
                        onChange={(e) => onUpload(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                        style={{ display: "none" }}
                      />
                    </label>
                    <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                      {uploadName || ".txt, .docx, .pdf"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* analyzing */}
          {isGenerating && (
            <div style={{ border: "1px solid var(--border)", borderRadius: 12, background: "var(--card)", padding: "20px 22px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <Globe style={{ width: 17, height: 17, color: "var(--spectral-violet)" }} strokeWidth={1.8} />
                <span style={{ fontSize: 15, fontWeight: 600, color: "var(--foreground)" }}>Reading {domain}...</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
                {STEP_DEF.map((s, i) => {
                  const done = i < step;
                  const active = i === step;
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 11 }}>
                      {done ? (
                        <CheckCircle2 style={{ width: 18, height: 18, color: "var(--signal-green)" }} strokeWidth={1.8} />
                      ) : active ? (
                        <span
                          className="bc-spin"
                          style={{ width: 18, height: 18, border: "2px solid var(--spectral-violet-20)", borderTopColor: "var(--spectral-violet)", borderRadius: 9999, flexShrink: 0, display: "inline-block" }}
                        />
                      ) : (
                        <span style={{ width: 18, height: 18, borderRadius: 9999, border: "2px solid var(--silver-mist)", flexShrink: 0, display: "inline-block" }} />
                      )}
                      <span style={{ fontSize: 13.5, color: done || active ? "var(--foreground)" : "var(--muted-foreground)", fontWeight: active ? 600 : 400 }}>
                        {done ? s.done : s.doing}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* done — extracted profile */}
        {showResult && !isGenerating && profile && (
          <div className="bc-fade">
            <div style={{ borderTop: "1px solid var(--border)", borderLeft: "3px solid var(--signal-green)", padding: "22px 26px", background: "var(--card)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
                <div>
                  <h3 style={{ fontSize: 17, fontWeight: 600, color: "var(--foreground)", margin: "0 0 3px" }}>Here's the voice I detected</h3>
                  <p style={{ fontSize: 13, color: "var(--muted-foreground)", margin: 0 }}>
                    Review the attributes, then save. You can fine-tune anything in Advanced Editing.
                  </p>
                </div>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: "var(--success-text)", background: "var(--success-bg)", border: "1px solid var(--success-border)", borderRadius: 9999, padding: "4px 11px" }}>
                  <Check style={{ width: 13, height: 13 }} strokeWidth={2.2} />
                  Ready to save
                </span>
              </div>

              <div style={{ marginBottom: 18 }}>
                <ExtractedAttrCards attrs={attrs} compact />
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  onClick={save}
                  style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 600, color: "var(--on-teal-text)", background: "var(--alpha-teal)", border: "none", borderRadius: 9, padding: "9px 16px", cursor: "pointer" }}
                >
                  {saved ? <Check style={{ width: 14, height: 14 }} strokeWidth={2} /> : <Save style={{ width: 14, height: 14 }} strokeWidth={1.9} />}
                  {saved ? "Saved" : "Save as active voice"}
                </button>
                <button
                  onClick={reset}
                  style={{ fontSize: 13, fontWeight: 600, color: "var(--foreground)", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 9, padding: "9px 16px", cursor: "pointer" }}
                >
                  Start over
                </button>
              </div>
            </div>

            {ba && (ba.beforeText || ba.afterText) && (
              <div style={{ borderTop: "1px solid var(--border)", padding: "22px 26px", background: "var(--card)" }}>
                <h3 style={{ fontSize: 15, fontWeight: 600, color: "var(--foreground)", margin: "0 0 14px" }}>See the difference</h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 14 }}>
                  <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: 16, background: "var(--muted)" }}>
                    <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--muted-foreground)", marginBottom: 9 }}>Before · generic AI</div>
                    <p style={{ fontSize: 14, lineHeight: 1.6, color: "var(--foreground)", margin: 0 }}>{ba.beforeText}</p>
                  </div>
                  <div style={{ border: "1px solid var(--alpha-teal-30)", borderRadius: 12, padding: 16, background: "var(--alpha-teal-5)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 9 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--teal-text)" }}>After · your voice</span>
                      <Sparkles style={{ width: 13, height: 13, color: "var(--teal-text)" }} strokeWidth={1.7} />
                    </div>
                    <p style={{ fontSize: 14, lineHeight: 1.6, color: "var(--foreground)", margin: 0 }}>{ba.afterText}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
